/** @format */

import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

interface SessionExpiredDialogProps {
	onLogin: () => void;
	onDismiss: () => void;
}

const buttonStyle = "text-base text-center font-roboto rounded-md cursor-pointer h-10 py-[6px] px-[12px]";

export const SessionExpiredDialog = ({ onLogin, onDismiss }: SessionExpiredDialogProps) => {
	const { t } = useTranslation();
	const loginButtonRef = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		loginButtonRef.current?.focus();
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				onDismiss();
			}
		};
		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [onDismiss]);

	return (
		<>
			<div className="fixed inset-0 bg-black/40 z-40" />
			<div
				role="alertdialog"
				aria-modal="true"
				aria-labelledby="session-expired-title"
				aria-describedby="session-expired-message"
				className="fixed w-full top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-white rounded-lg max-w-125 overflow-hidden border border-(--public-color-paper-modal-border)"
			>
				<header
					id="session-expired-title"
					className="flex p-4 w-full gap-2 text-[20px] text-(--public-color-text-filter) font-roboto border-b border-(--public-color-border-default) leading-[150%] items-center"
				>
					{t("sessionExpired.title")}
				</header>
				<div className="flex flex-col p-4 gap-3">
					<p id="session-expired-message" className="font-roboto text-[16px] text-(--public-color-text-default)">
						{t("sessionExpired.message")}
					</p>
					<p className="font-roboto text-[16px] font-semibold text-(--public-color-text-default)">
						{t("sessionExpired.saveWork")}
					</p>
					<p className="font-roboto text-[16px] text-(--public-color-text-default)">
						{t("sessionExpired.instruction")}
					</p>
				</div>
				<footer className="flex flex-wrap gap-2 w-full items-center justify-end p-3 border-t border-(--public-color-border-filter)">
					<button
						onClick={onDismiss}
						className={`bg-white border border-(--public-color-menu-label) text-(--public-color-menu-label) ${buttonStyle}`}
					>
						{t("sessionExpired.dismissButton")}
					</button>
					<button
						ref={loginButtonRef}
						onClick={onLogin}
						className={`border border-(--public-color-menu-label) text-white bg-(--public-color-menu-label) ${buttonStyle}`}
					>
						{t("sessionExpired.loginButton")}
					</button>
				</footer>
			</div>
		</>
	);
};
